// @flow
import { createAction } from 'redux-actions'
import { createActionMeta } from '../../util/redux'
import { logger } from '../../util/logger'
import { createDuck } from 'redux-duck'
import omit from 'lodash.omit'
import { update } from './util'

export * from './crud'
export * from './methods'
export * from './selectors'

// Types
import type { Action } from '../../util/redux'
import type { Role } from '../user/types'
import type {
  SharedLocation,
  State as LocationState,
  LocationModel
} from './types'

/**
 * Exports location redux module in `Ducks` format (including flowtypes)
 * @see {@link https://github.com/erikras/ducks-modular-redux}
 * @module ducks/location
 */

const location = createDuck('LOCATION', 'RACHIO')

// ACTION TYPES ///////////////////////////////////////////////////////////////

// Create
const LOCATION_CREATE_INIT = location.defineType('CREATE_INIT')
const LOCATION_CREATE_COMPLETE = location.defineType('CREATE_COMPLETE')
// Get
const LOCATION_GET_INIT = location.defineType('GET_INIT')
const LOCATION_GET_COMPLETE = location.defineType('GET_COMPLETE')
// Get Metadata
const LOCATION_GET_METADATA_INIT = location.defineType('GET_METADATA_INIT')
const LOCATION_GET_METADATA_COMPLETE = location.defineType(
  'GET_METADATA_COMPLETE'
)
// Update
const LOCATION_UPDATE_INIT = location.defineType('UPDATE_INIT')
const UPDATE = location.defineType('UPDATE')
const UPDATE_META = location.defineType('UPDATE_META')
const LOCATION_UPDATE_COMPLETE = location.defineType('UPDATE_COMPLETE')
// Delete
const LOCATION_DELETE_INIT = location.defineType('DELETE_INIT')
const REMOVE = location.defineType('REMOVE')
const LOCATION_DELETE_COMPLETE = location.defineType('DELETE_COMPLETE')
// Share
const LOCATION_SHARE_INIT = location.defineType('SHARE_INIT')
const LOCATION_SHARE_COMPLETE = location.defineType('SHARE_COMPLETE')
// Revoke/Remove Share
const LOCATION_REVOKE_SHARE_INIT = location.defineType('REVOKE_SHARE_INIT')
const LOCATION_REVOKE_SHARE_COMPLETE = location.defineType(
  'REVOKE_SHARE_COMPLETE'
)
const LOCATION_REMOVE_SHARE = location.defineType('REMOVE_SHARE')
// Validate share code
const VALIDATE_SHARE_CODE_INIT = location.defineType('VALIDATE_SHARE_CODE_INIT')
const VALIDATE_SHARE_CODE_COMPLETE = location.defineType(
  'VALIDATE_SHARE_CODE_COMPLETE'
)
// Get Schedules
const LOCATION_GET_SCHEDULES_INIT = location.defineType('GET_SCHEDULES_INIT')
const LOCATION_GET_SCHEDULES_COMPLETE = location.defineType(
  'GET_SCHEDULES_COMPLETE'
)
// Get weather forecasts
const LOCATION_GET_WEATHER_INIT = location.defineType('GET_WEATHER_INIT')
const LOCATION_GET_WEATHER_COMPLETE = location.defineType(
  'GET_WEATHER_COMPLETE'
)
// Get current weather
const LOCATION_GET_CURRENT_WEATHER_INIT = location.defineType(
  'GET_CURRENT_WEATHER_INIT'
)
const LOCATION_GET_CURRENT_WEATHER_COMPLETE = location.defineType(
  'GET_CURRENT_WEATHER_COMPLETE'
)
// Get schedule calendar
const LOCATION_GET_DAY_CALENDAR_INIT = location.defineType(
  'GET_DAY_CALENDAR_INIT'
)
const LOCATION_GET_DAY_CALENDAR_COMPLETE = location.defineType(
  'GET_DAY_CALENDAR_COMPLETE'
)
// Update location favorite
const LOCATION_UPDATE_FAVORITE_INIT = location.defineType(
  'UPDATE_FAVORITE_INIT'
)
const LOCATION_UPDATE_FAVORITE_COMPLETE = location.defineType(
  'UPDATE_FAVORITE_COMPLETE'
)
// Update location weather station
const LOCATION_UPDATE_WEATHER_STATION_INIT = location.defineType(
  'UPDATE_WEATHER_STATION_INIT'
)
const LOCATION_UPDATE_WEATHER_STATION_COMPLETE = location.defineType(
  'UPDATE_WEATHER_STATION_COMPLETE'
)
// Update location weather station
const LOCATION_GET_WEATHER_STATIONS_INIT = location.defineType(
  'GET_WEATHER_STATION_INIT'
)
const LOCATION_GET_WEATHER_STATIONS_COMPLETE = location.defineType(
  'GET_WEATHER_STATION_COMPLETE'
)
// Get watering days
const LOCATION_GET_WATERING_DAYS_INIT = location.defineType(
  'LOCATION_GET_WATERING_DAYS_INIT'
)
const LOCATION_GET_WATERING_DAYS_COMPLETE = location.defineType(
  'LOCATION_GET_WATERING_DAYS_COMPLETE'
)
// Util
const CLEAR_SOM_BASED_DATA = location.defineType('CLEAR_SOM_BASED_DATA')

// ACTION FLOWTYPES ///////////////////////////////////////////////////////////

// Delete
export type LocationDeleteInitAction = {|
  type: 'LOCATION_DELETE_INIT',
  payload: {
    locationId: string
  },
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

export type LocationDeleteCompleteAction = {|
  type: 'LOCATION_DELETE_COMPLETE',
  payload: {
    locationId: string,
    deleted?: boolean
  },
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

// Share
export type LocationShareInitAction = {|
  type: 'LOCATION_SHARE_INIT',
  payload: {
    locationId: string,
    type: Role,
    emailAddress: string,
    expires?: string
  },
  meta: {
    actionCreatedAt: number
  }
|}

export type LocationShareCompleteAction = {|
  type: 'LOCATION_SHARE_COMPLETE',
  payload:
    | {
        sharedLocation: SharedLocation,
        message: string,
        userExists: boolean
      }
    | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

// Revoke/Remove share
export type LocationRevokeShareInitAction = {|
  type: 'LOCATION_REVOKE_SHARE_INIT',
  payload:
    | {
        locationId: string,
        accountSharedWith: string
      }
    | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

export type LocationRevokeShareCompleteAction = {|
  type: 'LOCATION_SHARE_COMPLETE',
  payload: { deleted: boolean } | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

export type LocationRemoveShareAction = {|
  type: 'LOCATION_REMOVE_SHARE',
  payload: {|
    locationId: string,
    emailAddress: string
  |},
  error: false,
  meta: {
    actionCreatedAt: number
  }
|}

// Validate share code
export type ValidateShareCodeInitAction = {|
  type: 'VALIDATE_SHARE_CODE_INIT',
  payload: {
    locationId: string,
    code: string
  },
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

export type ValidateShareCodeCompleteAction = {|
  type: 'VALIDATE_SHARE_CODE_COMPLETE',
  payload: { locationSummary: Object } | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

// Update
export type LocationUpdateInitAction = {|
  type: 'LOCATION_UPDATE_INIT',
  payload: Object | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

export type UpdateAction = {|
  type: 'UPDATE',
  payload: {
    location: Location
  },
  error?: false,
  meta: {
    actionCreatedAt: number
  }
|}

export type UpdateMetaAction = {|
  type: 'UPDATE_META',
  payload: {
    locationId: string,
    data: Object
  },
  error?: false,
  meta: {
    actionCreatedAt: number
  }
|}

export type LocationUpdateCompleteAction = {|
  type: 'LOCATION_UPDATE_COMPLETE',
  payload:
    | {
        location: Object
      }
    | Error,
  error?: boolean,
  meta: {
    actionCreatedAt: number
  }
|}

// Remove
export type RemoveAction = {|
  type: 'REMOVE',
  payload: string,
  error?: false,
  meta: {
    actionCreatedAt: number
  }
|}

// ACTION CREATORS ////////////////////////////////////////////////////////////

export const locationCreateInit = createAction(LOCATION_CREATE_INIT)
export const locationCreateComplete = createAction(LOCATION_CREATE_COMPLETE)
export const locationGetInit = createAction(LOCATION_GET_INIT)
export const locationGetComplete = createAction(
  LOCATION_GET_COMPLETE,
  null,
  createActionMeta
)
// Delete
export const locationDeleteInit: ({
  locationId: string
}) => LocationDeleteInitAction = createAction(
  LOCATION_DELETE_INIT,
  null,
  createActionMeta
)
export const remove: (locationId: string) => RemoveAction = createAction(
  REMOVE,
  null,
  createActionMeta
)
export const locationDeleteComplete: Object => LocationDeleteCompleteAction = createAction(
  LOCATION_DELETE_COMPLETE,
  null,
  createActionMeta
)
// Share
export const locationShareInit: ({
  locationId: string,
  role: Role,
  emailAddress: string,
  expires?: string
}) => LocationShareInitAction = createAction(
  LOCATION_SHARE_INIT,
  null,
  createActionMeta
)
export const locationShareComplete: Object => LocationShareCompleteAction = createAction(
  LOCATION_SHARE_COMPLETE,
  null,
  createActionMeta
)
// Revoke/Remove Share
export const locationRevokeShareInit: ({
  locationId: string,
  emailAddress: string
}) => LocationRevokeShareInitAction = createAction(
  LOCATION_REVOKE_SHARE_INIT,
  null,
  createActionMeta
)
export const locationRevokeShareComplete: Object => LocationRevokeShareCompleteAction = createAction(
  LOCATION_REVOKE_SHARE_COMPLETE,
  null,
  createActionMeta
)
export const locationRemoveShare: ({
  locationId: string,
  emailAddress: string
}) => LocationRemoveShareAction = createAction(
  LOCATION_REMOVE_SHARE,
  null,
  createActionMeta
)
// Validate Share Code
export const validateShareCodeInit: ({
  locationId: string,
  code: string
}) => ValidateShareCodeInitAction = createAction(
  VALIDATE_SHARE_CODE_INIT,
  null,
  createActionMeta
)
export const validateShareCodeComplete: Object => ValidateShareCodeCompleteAction = createAction(
  VALIDATE_SHARE_CODE_COMPLETE,
  null,
  createActionMeta
)
// Update
export const locationUpdateInit: Object => LocationUpdateInitAction = createAction(
  LOCATION_UPDATE_INIT,
  null,
  createActionMeta
)
export const up: Object => UpdateAction = createAction(
  UPDATE,
  null,
  createActionMeta
)
export const updateMeta: Object => UpdateMetaAction = createAction(
  UPDATE_META,
  null,
  createActionMeta
)
export const locationUpdateComplete: Object => LocationUpdateCompleteAction = createAction(
  LOCATION_UPDATE_COMPLETE,
  null,
  createActionMeta
)
export const locationGetDayCalendarInit = createAction(
  LOCATION_GET_DAY_CALENDAR_INIT
)
export const locationGetDayCalendarComplete = createAction(
  LOCATION_GET_DAY_CALENDAR_COMPLETE
)
export const locationGetSchedulesInit = createAction(
  LOCATION_GET_SCHEDULES_INIT
)
export const locationGetSchedulesComplete = createAction(
  LOCATION_GET_SCHEDULES_COMPLETE
)
export const locationGetWeatherInit = createAction(
  LOCATION_GET_WEATHER_INIT,
  null,
  createActionMeta
)
export const locationGetWeatherComplete = createAction(
  LOCATION_GET_WEATHER_COMPLETE,
  null,
  createActionMeta
)
export const locationUpdateFavoriteInit = createAction(
  LOCATION_UPDATE_FAVORITE_INIT,
  null,
  createActionMeta
)
export const locationUpdateFavoriteComplete = createAction(
  LOCATION_UPDATE_FAVORITE_COMPLETE,
  null,
  createActionMeta
)
export const locationUpdateWeatherStationInit = createAction(
  LOCATION_UPDATE_WEATHER_STATION_INIT,
  null,
  createActionMeta
)
export const locationUpdateWeatherStationComplete = createAction(
  LOCATION_UPDATE_WEATHER_STATION_COMPLETE,
  null,
  createActionMeta
)
export const locationGetWeatherStationsInit = createAction(
  LOCATION_GET_WEATHER_STATIONS_INIT,
  null,
  createActionMeta
)
export const locationGetWeatherStationsComplete = createAction(
  LOCATION_GET_WEATHER_STATIONS_COMPLETE,
  null,
  createActionMeta
)
export const locationGetWateringDaysInit = createAction(
  LOCATION_GET_WATERING_DAYS_INIT,
  null,
  createActionMeta
)
export const locationGetWateringDaysComplete = createAction(
  LOCATION_GET_WATERING_DAYS_COMPLETE,
  null,
  createActionMeta
)
export const clearSOMData = createAction(
  CLEAR_SOM_BASED_DATA,
  null,
  createActionMeta
)

// REDUCER ////////////////////////////////////////////////////////////////////

const allowedMetaFields = [
  'sharedLocationGrants',
  'state',
  'owner',
  'weatherStations',
  'wateringDays',
  'weatherForecast'
]
const validateMetaData = data => {
  const valid =
    data &&
    Object.keys(data).every(
      datum => allowedMetaFields.includes(datum) || datum.includes('fetch')
    )

  if (!valid) {
    logger.error('Bad META update with data: ' + data)
  }

  return valid
}

export type Store = {
  +META: {
    +fetchingCalendar: boolean,
    +currentLocationId: ?string,
    +createStatus: ?(
      | 'CREATING'
      | 'LOCATION_CREATE_COMPLETE'
      | (Action & { error: true })
    )
  },
  +[location_id: string]: LocationModel
}

//
export const initialState = {
  META: {
    currentLocationId: null,
    createStatus: null, // ['CREATING', 'LOCATION_CREATE_COMPLETE', action]
    fetchingCalendar: false
  }
}

/* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.85.0. To view the error, delete this comment and run Flow. */
export default (state: Store = initialState, action = {}) => {
  const { payload: { locationId, data } = {} } = action
  const { [locationId]: location = {} } = state

  switch (action.type) {
    // Create
    case LOCATION_CREATE_INIT:
      return {
        ...state,
        META: { ...state.META, createStatus: 'CREATING' }
      }
    case LOCATION_CREATE_COMPLETE:
      if (action.error) {
        return {
          ...state,
          META: { ...state.META, createStatus: action }
        }
      } else {
        return {
          ...state,
          META: { ...state.META, createStatus: action },
          [action.payload.location.id]: action.payload.location
        }
      }
    // Get
    case LOCATION_GET_METADATA_INIT:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingMetadata: true
          }
        }
      }
    case LOCATION_GET_METADATA_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              fetchingMetadata: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              metadata: action.payload,
              fetchingMetadata: false
            }
          }
        }
      }
    // Update
    case UPDATE:
      return update(state, action)
    case UPDATE_META:
      validateMetaData(data)
      return {
        ...state,
        [locationId]: {
          ...location,
          META: {
            ...location.META,
            ...data
          }
        }
      }
    // Delete
    case REMOVE:
      return omit(state, action.payload)
    // Get schedules
    case LOCATION_GET_SCHEDULES_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.locationId]: { META: { fetchingSchedules: action } }
        }
      } else {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            scheduleIds: action.payload.scheduleIds,
            META: {
              ...(state[action.payload.locationId] || {}).META,
              fetchingSchedules: false
            }
          }
        }
      }
    // Get current weather
    case LOCATION_GET_CURRENT_WEATHER_INIT:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingCurrentWeather: true
          }
        }
      }
    case LOCATION_GET_CURRENT_WEATHER_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              fetchingCurrentWeather: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              currentWeather: action.payload.weather,
              currentWeatherLastPulled: action.meta.actionCreatedAt,
              fetchingCurrentWeather: false
            }
          }
        }
      }
    // Get weather forecast
    case LOCATION_GET_WEATHER_INIT:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingWeatherForecast: true
          }
        }
      }
    case LOCATION_GET_WEATHER_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              fetchingWeatherForecast: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              weatherForecast: action.payload.weatherForecast,
              fetchingWeatherForecast: false
            }
          }
        }
      }
    // Get schedule calendar
    case LOCATION_GET_DAY_CALENDAR_INIT:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingCalendar: true
          }
        }
      }
    case LOCATION_GET_DAY_CALENDAR_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.locationId]: {
            ...state[action.payload.locationId],
            META: {
              ...(state[action.payload.locationId] || {}).META,
              fetchingCalendar: action
            }
          }
        }
      } else {
        const location = state[action.payload.locationId]
        return {
          ...state,
          [action.payload.locationId]: {
            ...location,
            META: {
              ...(location || {}).META,
              runs: action.payload.runs,
              skips: action.payload.skips,
              fetchingCalendar: false
            }
          }
        }
      }
    // Remove Share
    case LOCATION_REMOVE_SHARE:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            sharedLocationGrants: (
              state[action.payload.locationId].META.sharedLocationGrants || []
            ).filter(
              slg =>
                !(
                  slg.locationId === action.payload.locationId &&
                  slg.emailAddress === action.payload.emailAddress &&
                  slg.role === action.payload.role
                )
            )
          }
        }
      }
    // Get watering days
    case LOCATION_GET_WATERING_DAYS_INIT:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingWateringDays: true
          }
        }
      }
    case LOCATION_GET_WATERING_DAYS_COMPLETE:
      return {
        ...state,
        [action.payload.locationId]: {
          ...state[action.payload.locationId],
          META: {
            ...(state[action.payload.locationId] || {}).META,
            fetchingWateringDays: false,
            wateringDays: action.payload.wateringDay
          }
        }
      }
    // Some data we get from the backend is based on a user's current SOM.
    // If the user changes their SOM we need to clear this data
    case CLEAR_SOM_BASED_DATA: {
      // $FlowFixMe
      const clonedStore: LocationState = Object.assign({}, state)
      const keys: Array<string> = Object.keys(clonedStore).filter(
        key => key !== 'META'
      )

      keys.forEach(key => {
        const curLocation = clonedStore[key]

        if (curLocation.META) {
          // Delete SOM based data
          delete curLocation.META.weatherForecast
        }
      })

      return clonedStore
    }
    default:
      return state
  }
}
